/**
 * チバリヨ - P4 顧客統合モーダル (Phase 3)
 *
 * 同じ正規化名を持つ重複顧客を1件に統合する。admin 専用 (P4 から起動)。
 *
 * 公開API:
 *   openMergeModal({
 *     group,                // 統合対象グループ (同一正規化名の active 顧客オブジェクト配列, 2件以上)
 *     currentClientId,      // 既定で「主(残す)」にする顧客ID (編集中の顧客)
 *     currentSessionMember, // 実行者 (member doc, .id=memberId / .name)
 *     membersById,          // Map<memberId, memberData> 主担当表示用
 *     onComplete,           // 統合完了時に呼ぶ (P4 が一覧を再読込)
 *   })
 *
 * 動作 (仕様 Phase 3):
 *   1. 選択ステップ: admin が「主(残す)」を1件選び、統合する重複(統合元)に
 *      チェック。既定で主以外は全て統合元。
 *   2. 確認ステップ: reports/tasks の付け替え件数、空欄補完される項目、
 *      再集計後の訪問回数/最終訪問日をプレビューしてから実行。
 *   3. 実行:
 *      - 統合元を参照する reports/tasks の client.clientId を主へ付け替え
 *        (client.text / client.clientText の履歴文言はそのまま保持)
 *      - 主の空欄項目を統合元の値で補完 (両方に値があれば主を優先)
 *      - 主の visitCount / lastVisitDate を reports から再集計
 *      - 統合元を論理削除 (active=false / mergedInto=主ID /
 *        archivedReason='統合済み' + 監査用 mergedAt/By)
 *      - mergeHistory に監査ログを追記
 *
 * Firestore:
 *   - ルール変更不要: admin は clients/reports/tasks を update 可能、
 *     mergeHistory は admin の create (mergedBy==uid) を許可済み。
 *   - インデックス変更不要: 付け替えは client.clientId == 統合元ID の単一等価
 *     クエリ (自動インデックス)。
 *   - writeBatch は最大500オペレーション。付け替え件数が多い場合に備えて
 *     450件ごとにバッチ分割する (グループ単位なので非アトミックだが冪等性あり)。
 */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

import { db, auth } from './firebase-init.js';

const BATCH_LIMIT = 450;

// 主の空欄を統合元から補完できる単純フィールド (notes/category 等)
// location (prefecture+municipality) と businessTypes は専用ロジックで扱う。
const BACKFILL_STRING_FIELDS = ['nameKana', 'category', 'notes'];

// =====================================================================
// モーダル状態
// =====================================================================
let modalState = null;

// =====================================================================
// 公開API
// =====================================================================
export function openMergeModal({
  group,
  currentClientId,
  currentSessionMember,
  membersById,
  onComplete,
}) {
  if (!Array.isArray(group) || group.length < 2) {
    console.warn('[チバリヨ P4 merge] group が不正です (2件以上必要)');
    return;
  }

  const survivorId = group.some((c) => c.id === currentClientId)
    ? currentClientId
    : group[0].id;

  modalState = {
    step: 'select',          // 'select' | 'confirm' | 'running'
    group,
    survivorId,
    loserIds: new Set(group.filter((c) => c.id !== survivorId).map((c) => c.id)),
    currentSessionMember: currentSessionMember || null,
    membersById: membersById || new Map(),
    onComplete: typeof onComplete === 'function' ? onComplete : () => {},
    reason: '',
    saving: false,
    plan: null,              // 確認ステップで算出した統合プラン
  };

  renderModal();
}

// =====================================================================
// 描画
// =====================================================================
function renderModal() {
  removeOverlayDom();

  const overlay = document.createElement('div');
  overlay.className = 'p4-merge-overlay';
  overlay.id = 'p4-merge-overlay';

  overlay.innerHTML = `
    <div class="p4-merge-modal" role="dialog" aria-modal="true">
      <div class="p4-merge-mhead">
        <h2 class="p4-merge-mtitle">顧客を統合</h2>
        <button type="button" class="p4-merge-close" id="p4-merge-close-x" aria-label="閉じる">×</button>
      </div>
      <div class="p4-merge-mbody" id="p4-merge-mbody"></div>
      <div class="p4-merge-mstatus" id="p4-merge-mstatus"></div>
      <div class="p4-merge-mfoot" id="p4-merge-mfoot"></div>
    </div>
  `;

  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) handleClose();
  });
  document.getElementById('p4-merge-close-x').addEventListener('click', handleClose);
  document.addEventListener('keydown', handleEscKey);

  renderStep();
}

function renderStep() {
  const bodyEl = document.getElementById('p4-merge-mbody');
  const footEl = document.getElementById('p4-merge-mfoot');
  if (!bodyEl || !footEl) return;

  if (modalState.step === 'select') {
    bodyEl.innerHTML = renderSelectBody();
    footEl.innerHTML = `
      <button type="button" class="btn btn-ghost" id="p4-merge-cancel">キャンセル</button>
      <button type="button" class="btn btn-primary" id="p4-merge-next">確認へ</button>
    `;
    attachSelectEvents();
  } else if (modalState.step === 'confirm') {
    bodyEl.innerHTML = renderConfirmBody();
    footEl.innerHTML = `
      <button type="button" class="btn btn-ghost" id="p4-merge-back">戻る</button>
      <button type="button" class="btn btn-danger" id="p4-merge-exec">統合を実行</button>
    `;
    attachConfirmEvents();
  }
}

// ----- 選択ステップ -----
function renderSelectBody() {
  const { group, survivorId, loserIds } = modalState;

  const rows = group.map((c) => {
    const isSurvivor = c.id === survivorId;
    const isLoser = loserIds.has(c.id);
    const area = c.area || '';
    const member = c.primaryMemberId ? modalState.membersById.get(c.primaryMemberId) : null;
    const memberName = member ? member.name : '';
    const visitCount = c.visitCount || 0;

    return `
      <div class="p4-merge-row ${isSurvivor ? 'is-survivor' : ''}" data-id="${escapeAttr(c.id)}">
        <label class="p4-merge-pick">
          <input type="radio" name="p4-merge-survivor" value="${escapeAttr(c.id)}" ${isSurvivor ? 'checked' : ''}>
          <span class="p4-merge-pick-label">主にする</span>
        </label>
        <div class="p4-merge-rowinfo">
          <div class="p4-merge-rowline1">
            <span class="p4-merge-rowname">${escapeHtml(c.name || '(名前未設定)')}</span>
            ${c.nameKana ? `<span class="p4-merge-rowkana">${escapeHtml(c.nameKana)}</span>` : ''}
            ${isSurvivor ? '<span class="p4-merge-badge-survivor">主</span>' : ''}
          </div>
          <div class="p4-merge-rowline2">
            <span class="${area ? '' : 'p4-merge-empty'}">${area ? escapeHtml(area) : '所在地未設定'}</span>
            <span class="${memberName ? '' : 'p4-merge-empty'}">${memberName ? '担当: ' + escapeHtml(memberName) : '担当未割当'}</span>
            <span>訪問 ${visitCount}回</span>
            ${c.lastVisitDate ? `<span>最終 ${escapeHtml(c.lastVisitDate)}</span>` : ''}
          </div>
        </div>
        <label class="p4-merge-loser ${isSurvivor ? 'is-hidden' : ''}">
          <input type="checkbox" class="p4-merge-loser-cb" value="${escapeAttr(c.id)}" ${isLoser ? 'checked' : ''}>
          <span>統合する</span>
        </label>
      </div>
    `;
  }).join('');

  return `
    <p class="p4-merge-desc">
      残す顧客(<strong>主</strong>)を1件選び、これに統合する重複顧客にチェックしてください。<br>
      統合元の日報・タスクは主へ付け替えられ、統合元はアーカイブされます。
    </p>
    <div class="p4-merge-rows">${rows}</div>
  `;
}

function attachSelectEvents() {
  const overlay = document.getElementById('p4-merge-overlay');
  if (!overlay) return;

  overlay.querySelectorAll('input[name="p4-merge-survivor"]').forEach((r) => {
    r.addEventListener('change', (e) => {
      modalState.survivorId = e.target.value;
      // 主に選ばれた顧客は統合元から外す。それ以外は既定で統合元(チェックON)。
      modalState.loserIds = new Set(
        modalState.group.filter((c) => c.id !== modalState.survivorId).map((c) => c.id)
      );
      renderStep();
    });
  });

  overlay.querySelectorAll('.p4-merge-loser-cb').forEach((cb) => {
    cb.addEventListener('change', (e) => {
      const id = e.target.value;
      if (e.target.checked) modalState.loserIds.add(id);
      else modalState.loserIds.delete(id);
    });
  });

  document.getElementById('p4-merge-cancel').addEventListener('click', handleClose);
  document.getElementById('p4-merge-next').addEventListener('click', goToConfirm);
}

// ----- 確認ステップ -----
async function goToConfirm() {
  if (modalState.loserIds.size === 0) {
    setStatus('統合する重複顧客を1件以上選択してください', 'error');
    return;
  }

  setButtonsDisabled(true);
  setStatus('集計中…', 'info');

  try {
    modalState.plan = await buildMergePlan();
    modalState.step = 'confirm';
    setStatus('', 'info');
    renderStep();
  } catch (err) {
    console.error('[チバリヨ P4 merge] プラン算出失敗:', err);
    setStatus('集計に失敗しました: ' + (err.message || err), 'error');
    setButtonsDisabled(false);
  }
}

// 統合プランの算出: 付け替え対象 reports/tasks の収集、補完項目、再集計値
async function buildMergePlan() {
  const survivor = modalState.group.find((c) => c.id === modalState.survivorId);
  const losers = modalState.group.filter((c) => modalState.loserIds.has(c.id));

  // ----- reports / tasks の収集 -----
  // 主自身の reports も訪問回数の再集計に使うため取得する。
  const reportRefs = [];   // 付け替えが必要な loser の report ref
  const taskRefs = [];     // 付け替えが必要な loser の task ref
  let allVisitDates = [];  // 主 + 統合元の全 report 日付 (訪問回数=件数)
  let survivorReportCount = 0;

  for (const c of [survivor, ...losers]) {
    const rSnap = await getDocs(
      query(collection(db, 'reports'), where('client.clientId', '==', c.id))
    );
    rSnap.forEach((d) => {
      const date = d.get('date');
      if (typeof date === 'string' && date) allVisitDates.push(date);
      if (c.id !== survivor.id) reportRefs.push(d.ref);
    });
    if (c.id === survivor.id) survivorReportCount = rSnap.size;

    if (c.id !== survivor.id) {
      const tSnap = await getDocs(
        query(collection(db, 'tasks'), where('client.clientId', '==', c.id))
      );
      tSnap.forEach((d) => taskRefs.push(d.ref));
    }
  }

  // ----- 訪問回数 / 最終訪問日の再集計 -----
  // 訪問回数 = 主に集約後の reports 件数を基本とする。
  //   ただし保存済み visitCount の合計が reports 件数を上回る場合 (旧データで
  //   reports 化されていない訪問が visitCount に積まれているケース) は、件数を
  //   減らさないよう合計値を下限にする。prod は visitCount が 0 据え置きなので
  //   実質 reports 件数になる。
  const sumStoredVisitCount = [survivor, ...losers]
    .reduce((s, c) => s + (typeof c.visitCount === 'number' && c.visitCount > 0 ? c.visitCount : 0), 0);
  const newVisitCount = Math.max(allVisitDates.length, sumStoredVisitCount);
  // 最終訪問日 = reports 日付の最大 ∪ グループ各社の保存済み lastVisitDate の最大 (安全側)
  let newLastVisitDate = allVisitDates.reduce((m, d) => (d > m ? d : m), '');
  for (const c of [survivor, ...losers]) {
    if (typeof c.lastVisitDate === 'string' && c.lastVisitDate > newLastVisitDate) {
      newLastVisitDate = c.lastVisitDate;
    }
  }
  if (!newLastVisitDate) newLastVisitDate = null;

  // ----- 空欄補完 -----
  const backfill = computeBackfill(survivor, losers);

  return {
    survivor,
    losers,
    reportRefs,
    taskRefs,
    reassignReports: reportRefs.length,
    reassignTasks: taskRefs.length,
    survivorReportCount,
    newVisitCount,
    newLastVisitDate,
    backfill,
  };
}

// 主の空欄を統合元の値で補完する内容を算出 (両方に値があれば主を優先)
//   返り値: { fields: {<key>:<value>}, labels: [{label, value, fromName}] }
function computeBackfill(survivor, losers) {
  const fields = {};
  const labels = [];

  const isBlankStr = (v) => !v || !String(v).trim();

  // 文字列フィールド
  for (const key of BACKFILL_STRING_FIELDS) {
    if (!isBlankStr(survivor[key])) continue;
    for (const l of losers) {
      if (!isBlankStr(l[key])) {
        fields[key] = String(l[key]).trim();
        labels.push({ label: labelOf(key), value: fields[key], fromName: l.name || '(名前未設定)' });
        break;
      }
    }
  }

  // 所在地 (prefecture + municipality はセットで扱う)
  if (isBlankStr(survivor.prefecture) && isBlankStr(survivor.municipality)) {
    for (const l of losers) {
      if (!isBlankStr(l.prefecture) || !isBlankStr(l.municipality)) {
        fields.prefecture = (l.prefecture || '').trim();
        fields.municipality = (l.municipality || '').trim();
        fields.area = generateArea(fields.prefecture, fields.municipality);
        labels.push({ label: '所在地', value: fields.area, fromName: l.name || '(名前未設定)' });
        break;
      }
    }
  }

  // 提案種別 (配列。主が空なら統合元の非空を採用)
  if (!Array.isArray(survivor.businessTypes) || survivor.businessTypes.length === 0) {
    for (const l of losers) {
      if (Array.isArray(l.businessTypes) && l.businessTypes.length > 0) {
        fields.businessTypes = [...l.businessTypes];
        labels.push({ label: '提案種別', value: fields.businessTypes.join('・'), fromName: l.name || '(名前未設定)' });
        break;
      }
    }
  }

  // 主担当 (主が未割当なら統合元から採用)
  if (!survivor.primaryMemberId) {
    for (const l of losers) {
      if (l.primaryMemberId) {
        fields.primaryMemberId = l.primaryMemberId;
        const m = modalState.membersById.get(l.primaryMemberId);
        labels.push({ label: '主担当', value: m ? m.name : '(不明)', fromName: l.name || '(名前未設定)' });
        break;
      }
    }
  }

  // 訪問途絶日数 (主が未設定=null なら統合元から採用)
  const survivorNvd = (typeof survivor.noVisitDays === 'number' && survivor.noVisitDays > 0) ? survivor.noVisitDays : null;
  if (survivorNvd == null) {
    for (const l of losers) {
      const v = (typeof l.noVisitDays === 'number' && l.noVisitDays > 0) ? l.noVisitDays : null;
      if (v != null) {
        fields.noVisitDays = v;
        labels.push({ label: '訪問途絶日数', value: v + '日', fromName: l.name || '(名前未設定)' });
        break;
      }
    }
  }

  return { fields, labels };
}

function renderConfirmBody() {
  const { plan, membersById } = modalState;
  const survivor = plan.survivor;
  const member = survivor.primaryMemberId ? membersById.get(survivor.primaryMemberId) : null;

  const losersHtml = plan.losers.map((l) => `
    <li>${escapeHtml(l.name || '(名前未設定)')}<span class="p4-merge-empty">${l.area ? ' / ' + escapeHtml(l.area) : ''}</span></li>
  `).join('');

  const backfillHtml = plan.backfill.labels.length > 0
    ? `
      <div class="p4-merge-confbox">
        <div class="p4-merge-conftitle">主に補完される項目</div>
        <ul class="p4-merge-conflist">
          ${plan.backfill.labels.map((b) => `
            <li><span class="p4-merge-conflabel">${escapeHtml(b.label)}</span>
              <span class="p4-merge-confval">${escapeHtml(b.value)}</span>
              <span class="p4-merge-conffrom">← ${escapeHtml(b.fromName)}</span></li>
          `).join('')}
        </ul>
      </div>
    `
    : `<div class="p4-merge-confbox p4-merge-empty">主の空欄を補完できる項目はありません (主の登録内容はそのまま)。</div>`;

  const oldVisit = survivor.visitCount || 0;
  const visitChanged = oldVisit !== plan.newVisitCount;

  return `
    <div class="p4-merge-confhead">
      <div class="p4-merge-confkeep">
        <span class="p4-merge-confkeep-label">残す顧客(主)</span>
        <span class="p4-merge-confkeep-name">${escapeHtml(survivor.name || '(名前未設定)')}</span>
        <span class="p4-merge-empty">${member ? '担当: ' + escapeHtml(member.name) : '担当未割当'}</span>
      </div>
    </div>

    <div class="p4-merge-confbox">
      <div class="p4-merge-conftitle">統合してアーカイブする顧客 (${plan.losers.length}件)</div>
      <ul class="p4-merge-conflist p4-merge-losers">${losersHtml}</ul>
    </div>

    <div class="p4-merge-confbox">
      <div class="p4-merge-conftitle">主へ付け替えるデータ</div>
      <div class="p4-merge-stats">
        <span>日報 <strong>${plan.reassignReports}</strong> 件</span>
        <span>タスク <strong>${plan.reassignTasks}</strong> 件</span>
        <span>訪問回数 ${oldVisit}回 → <strong class="${visitChanged ? 'p4-merge-hl' : ''}">${plan.newVisitCount}回</strong></span>
        <span>最終訪問 <strong>${plan.newLastVisitDate ? escapeHtml(plan.newLastVisitDate) : 'なし'}</strong></span>
      </div>
    </div>

    ${backfillHtml}

    <div class="p4-merge-confbox">
      <label class="p4-merge-label" for="p4-merge-reason">統合理由 <span class="p4-merge-opt">(任意)</span></label>
      <input id="p4-merge-reason" type="text" class="p4-merge-input" maxlength="200"
             placeholder="例: 表記ゆれの重複登録" value="${escapeAttr(modalState.reason)}">
    </div>

    <p class="p4-merge-warn">
      ⚠️ 統合元 ${plan.losers.length}件はアーカイブされ「統合済み」として記録されます。
      この操作後も統合元は復活できますが、付け替えたデータは自動では戻りません。
    </p>
  `;
}

function attachConfirmEvents() {
  document.getElementById('p4-merge-back').addEventListener('click', () => {
    if (modalState.saving) return;
    modalState.step = 'select';
    setStatus('', 'info');
    renderStep();
  });

  const reasonEl = document.getElementById('p4-merge-reason');
  if (reasonEl) {
    reasonEl.addEventListener('input', (e) => { modalState.reason = e.target.value; });
  }

  document.getElementById('p4-merge-exec').addEventListener('click', executeMerge);
}

// =====================================================================
// 実行
// =====================================================================
async function executeMerge() {
  if (!modalState || modalState.saving) return;
  const plan = modalState.plan;
  if (!plan) return;

  modalState.saving = true;
  modalState.step = 'running';
  setButtonsDisabled(true);
  setStatus('統合処理中…', 'info');

  try {
    const survivorId = plan.survivor.id;
    const reason = (modalState.reason || '').trim() || '統合済み';
    const memberId = modalState.currentSessionMember?.id || null;
    const memberName = modalState.currentSessionMember?.name || '';
    const uid = auth.currentUser ? auth.currentUser.uid : null;

    // ----- 全書き込みオペレーションを組み立て -----
    const ops = [];

    // 1) reports / tasks の付け替え (client.clientId のみ更新、文言は保持)
    for (const ref of plan.reportRefs) {
      ops.push((batch) => batch.update(ref, {
        'client.clientId': survivorId,
        updatedAt: serverTimestamp(),
      }));
    }
    for (const ref of plan.taskRefs) {
      ops.push((batch) => batch.update(ref, {
        'client.clientId': survivorId,
        updatedAt: serverTimestamp(),
      }));
    }

    // 2) 主の更新 (空欄補完 + 訪問再集計)
    const survivorUpdate = {
      ...plan.backfill.fields,
      visitCount: plan.newVisitCount,
      lastVisitDate: plan.newLastVisitDate,
      updatedAt: serverTimestamp(),
    };
    survivorUpdate.isPartiallyFilled = computeIsPartiallyFilled(plan.survivor, plan.backfill.fields);
    ops.push((batch) => batch.update(doc(db, 'clients', survivorId), survivorUpdate));

    // 3) 統合元の論理削除
    for (const l of plan.losers) {
      ops.push((batch) => batch.update(doc(db, 'clients', l.id), {
        active: false,
        mergedInto: survivorId,
        archivedAt: serverTimestamp(),
        archivedBy: memberId,
        archivedReason: reason,
        mergedAt: serverTimestamp(),
        mergedBy: memberId,
        mergedByName: memberName,
        unarchivedAt: null,
        unarchivedBy: null,
        unarchivedReason: null,
        updatedAt: serverTimestamp(),
      }));
    }

    // 4) mergeHistory に監査ログ
    const historyRef = doc(collection(db, 'mergeHistory'));
    ops.push((batch) => batch.set(historyRef, {
      survivorId,
      survivorName: plan.survivor.name || '',
      loserIds: plan.losers.map((l) => l.id),
      loserNames: plan.losers.map((l) => l.name || ''),
      reassignedReports: plan.reassignReports,
      reassignedTasks: plan.reassignTasks,
      newVisitCount: plan.newVisitCount,
      newLastVisitDate: plan.newLastVisitDate,
      backfilledFields: Object.keys(plan.backfill.fields),
      reason,
      mergedBy: uid,           // ルール: request.auth.uid と一致が必要
      mergedByMemberId: memberId,
      mergedByName: memberName,
      createdAt: serverTimestamp(),
    }));

    // ----- バッチ分割して実行 -----
    await commitInBatches(ops);

    setStatus(`統合しました (日報${plan.reassignReports}件 / タスク${plan.reassignTasks}件を付け替え)`, 'success');

    setTimeout(() => {
      const cb = modalState?.onComplete;
      closeOverlayElement();
      cb();
    }, 800);
  } catch (err) {
    console.error('[チバリヨ P4 merge] 統合失敗:', err);
    setStatus('統合に失敗しました: ' + (err.message || err), 'error');
    modalState.saving = false;
    modalState.step = 'confirm';
    setButtonsDisabled(false);
  }
}

// オペレーション配列を BATCH_LIMIT ごとに分割して逐次コミット
async function commitInBatches(ops) {
  for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const op of ops.slice(i, i + BATCH_LIMIT)) op(batch);
    await batch.commit();
  }
}

// =====================================================================
// 開閉・状態
// =====================================================================
function handleClose() {
  if (modalState && modalState.saving) return;  // 実行中は閉じない
  closeOverlayElement();
}

function handleEscKey(e) {
  if (e.key === 'Escape') handleClose();
}

function closeOverlayElement() {
  document.removeEventListener('keydown', handleEscKey);
  removeOverlayDom();
  modalState = null;
}

function removeOverlayDom() {
  const overlay = document.getElementById('p4-merge-overlay');
  if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
}

function setButtonsDisabled(disabled) {
  const footEl = document.getElementById('p4-merge-mfoot');
  if (!footEl) return;
  footEl.querySelectorAll('button').forEach((b) => { b.disabled = disabled; });
  const closeX = document.getElementById('p4-merge-close-x');
  if (closeX) closeX.disabled = disabled;
}

function setStatus(msg, level) {
  const el = document.getElementById('p4-merge-mstatus');
  if (!el) return;
  el.textContent = msg || '';
  el.className = 'p4-merge-mstatus is-' + (level || 'info');
}

// =====================================================================
// ヘルパー
// =====================================================================
function labelOf(key) {
  return ({
    nameKana: 'カナ',
    category: 'カテゴリ',
    notes: '備考',
  })[key] || key;
}

function generateArea(prefecture, municipality) {
  if (!prefecture && !municipality) return '';
  if (!municipality) return prefecture;
  if (!prefecture) return municipality;
  return prefecture + municipality;
}

// 補完後の主の情報不足判定 (P4 の computeIsPartiallyFilled と同基準)
function computeIsPartiallyFilled(survivor, backfill) {
  const kana = backfill.nameKana != null ? backfill.nameKana : survivor.nameKana;
  const pref = backfill.prefecture != null ? backfill.prefecture : survivor.prefecture;
  const muni = backfill.municipality != null ? backfill.municipality : survivor.municipality;
  const bt = backfill.businessTypes != null ? backfill.businessTypes : survivor.businessTypes;
  if (!kana) return true;
  if (!pref && !muni) return true;
  if (!Array.isArray(bt) || bt.length === 0) return true;
  return false;
}

function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function escapeAttr(str) {
  return escapeHtml(str);
}
